import {
  CopyStrategyConfigDto,
  getActivity,
  getLeaderOverview,
  getPositions,
  getWallet,
  LeaderPositionDto,
  LeaderPositionsGroupDto,
  WalletResponse,
} from 'api/copyBotApi';
import axios from 'axios';
import { initialRequestState } from 'consts';
import { makeAutoObservable, reaction } from 'mobx';
import { CommonStore } from 'store/common/CommonStore';
import { Web3Store } from 'store/web3/Web3Store';
import { RequestState } from 'types';
import { Leader, PortfolioResponse, Position, TradesResponse } from 'types/portfolio';
import { setPartialDataToObject } from 'utils/helpers';
import { Address } from 'viem';

const EMPTY_TRADES = {
  owner: '0x0000000000000000000000000000000000000000' as Address,
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  },
  trades: [],
};

export type Tab = 'wallet' | 'wallets' | 'positions' | 'activity' | 'settings';

const normalizePosition = (position: LeaderPositionDto): Position => ({
  id: `${position.leader.address}-${position.conditionId}-${position.outcome}`,
  leader: position.leader.address,
  leaderName: position.leader.name || position.leader.pseudonym,
  leaderProfileImage: position.leader.profileImageOptimized || position.leader.profileImage,
  leaderEnabled: position.leader.enabled ?? true,
  asset: position.asset,
  conditionId: position.conditionId,
  title: position.title,
  slug: position.slug,
  icon: position.icon,
  eventSlug: position.eventSlug,
  outcome: position.outcome,
  size: position.size,
  avgPrice: position.avgPrice,
  currentPrice: position.currentPrice,
  initialValue: position.initialValue,
  currentValue: position.currentValue,
  unrealizedPnl: position.unrealizedPnl,
  realizedPnl: position.realizedPnl,
  totalPnl: position.totalPnl,
  roi: position.roi,
  percentPnl: position.percentPnl,
  percentRealizedPnl: position.percentRealizedPnl,
  isOpen: position.isOpen,
  closedAt: position.closedAt,
  closeReason: position.closeReason,
  closedBySettlement: position.closedBySettlement,
});

const normalizeTrade = (position: LeaderPositionDto) => ({
  id: `${position.leader.address}-${position.conditionId}-${position.outcome}-${position.closedAt || 'open'}`,
  leader: position.leader.address,
  leaderName: position.leader.name || position.leader.pseudonym,
  leaderProfileImage: position.leader.profileImageOptimized || position.leader.profileImage,
  title: position.title,
  icon: position.icon,
  eventSlug: position.eventSlug,
  outcome: position.outcome,
  currentValue: position.currentValue,
  initialValue: position.initialValue,
  totalPnl: position.totalPnl,
  roi: position.roi,
  isOpen: position.isOpen,
  closedAt: position.closedAt,
  closeReason: position.closeReason,
  closedBySettlement: position.closedBySettlement,
  conditionId: position.conditionId,
});

const normalizeLeader = (leader: LeaderPositionsGroupDto): Leader => ({
  address: leader.leader.address,
  name: leader.leader.name,
  pseudonym: leader.leader.pseudonym,
  profileImage: leader.leader.profileImage,
  profileImageOptimized: leader.leader.profileImageOptimized,
  enabled: leader.leader.enabled ?? true,
  positionsCount: leader.positionsCount,
  totalCurrentValue: leader.totalCurrentValue,
  totalInitialValue: leader.totalInitialValue,
  unrealizedPnl: leader.unrealizedPnl,
  realizedPnl: leader.realizedPnl,
  totalPnl: leader.totalPnl,
  roi: leader.roi,
  positions: [],
});

const mergeLeaders = (overview: Leader[], wallet: WalletResponse): Leader[] => {
  const overviewMap = new Map(overview.map((leader) => [leader.address.toLowerCase(), leader]));

  return wallet.leaders.map((leader) => {
    const normalized = overviewMap.get(leader.address.toLowerCase());
    if (normalized) {
      return {
        ...normalized,
        enabled: leader.enabled ?? normalized.enabled,
      };
    }

    return {
      address: leader.address,
      enabled: leader.enabled ?? true,
      positionsCount: 0,
      totalCurrentValue: 0,
      totalInitialValue: 0,
      unrealizedPnl: 0,
      realizedPnl: 0,
      totalPnl: 0,
      roi: 0,
      positions: [],
    };
  });
};

const normalizePortfolio = (
  wallet: WalletResponse,
  overview: LeaderPositionsGroupDto[],
  positionItems: LeaderPositionDto[]
): PortfolioResponse => {
  const leaders = mergeLeaders(overview.map(normalizeLeader), wallet);
  const positions = positionItems.map(normalizePosition);

  return {
    id: wallet.id,
    owner: wallet.owner,
    signerAddress: wallet.signerAddress,
    proxyWallet: wallet.proxyWallet,
    enabled: wallet.enabled,
    strategy: wallet.copyStrategyConfig.strategy,
    copySize: wallet.copyStrategyConfig.copySize,
    adaptiveMinPercent: wallet.copyStrategyConfig.adaptiveMinPercent,
    adaptiveMaxPercent: wallet.copyStrategyConfig.adaptiveMaxPercent,
    adaptiveThreshold: wallet.copyStrategyConfig.adaptiveThreshold,
    tieredMultipliers: wallet.copyStrategyConfig.tieredMultipliers,
    tradeMultiplier: wallet.copyStrategyConfig.tradeMultiplier,
    minLeaderTradeSizeUSD: wallet.copyStrategyConfig.minLeaderTradeSizeUSD,
    maxOrderSizeUSD: wallet.copyStrategyConfig.maxOrderSizeUSD,
    minOrderSizeUSD: wallet.copyStrategyConfig.minOrderSizeUSD,
    maxPositionSizeUSD: wallet.copyStrategyConfig.maxPositionSizeUSD,
    maxDailyVolumeUSD: wallet.copyStrategyConfig.maxDailyVolumeUSD,
    deposit: wallet.summary.deposit,
    totalTradeValue: wallet.summary.totalTradeValue,
    openPositionsValue: wallet.summary.openPositionsValue,
    unrealizedPnl: wallet.summary.unrealizedPnl,
    realizedPnl: wallet.summary.realizedPnl,
    totalPnl: wallet.summary.pnl,
    roi: wallet.summary.roi,
    leaders,
    positions,
    hasPrivateKey: wallet.hasPrivateKey,
    setup: wallet.setup,
    createdAt: wallet.createdAt,
    updatedAt: wallet.updatedAt,
  };
};

const loadSupplementaryData = async (owner: Address) => {
  const [overviewResult, positionsResult] = await Promise.allSettled([
    getLeaderOverview(owner),
    getPositions(owner),
  ]);

  const overview = overviewResult.status === 'fulfilled' ? overviewResult.value.leaders : [];
  const positions = positionsResult.status === 'fulfilled' ? positionsResult.value.positions : [];

  if (overviewResult.status === 'rejected') {
    console.error('Failed to load leader overview:', overviewResult.reason);
  }

  if (positionsResult.status === 'rejected') {
    console.error('Failed to load positions:', positionsResult.reason);
  }

  return { overview, positions };
};

export class PortfolioStore {
  tab: Tab = 'wallet';

  portfolioResponse: RequestState<PortfolioResponse> = initialRequestState;
  tradesResponse: RequestState<TradesResponse> = {
    ...initialRequestState,
    data: EMPTY_TRADES,
  };

  constructor(
    private web3: Web3Store,
    private commonStore: CommonStore
  ) {
    makeAutoObservable(this, {}, { autoBind: true });

    reaction(
      () => this.web3.account,
      () => {
        this.fetchPortfolio();
      }
    );

    reaction(
      () => this.commonStore.mode,
      () => {
        if (
          (this.commonStore.mode === 'cockpit' || this.commonStore.mode === 'pilot') &&
          !this.portfolio &&
          !this.portfolioResponse.isLoading
        ) {
          this.fetchPortfolio();
        }
      }
    );

    reaction(
      () => this.tab,
      () => {
        if (this.tab === 'activity' && !this.tradesResponse.isLoading) {
          this.fetchTrades();
        }
      }
    );
  }

  get portfolio() {
    return this.portfolioResponse.data;
  }

  get wallets() {
    return this.portfolio
      ? [...this.portfolio.leaders].sort((a, b) => b.totalPnl - a.totalPnl)
      : [];
  }

  get positions() {
    return this.portfolio?.positions || [];
  }

  get trades() {
    return this.tradesResponse.data?.trades || [];
  }

  updatePortfolio = () => {
    this.fetchPortfolio(true);
  };

  resetPortfolio = () => {
    this.setPortfolio(initialRequestState);
    this.setTrades({ ...initialRequestState, data: EMPTY_TRADES });
  };

  setPortfolio = (state: Partial<RequestState<PortfolioResponse>>) => {
    setPartialDataToObject(this.portfolioResponse, state);
  };

  setTrades = (state: Partial<RequestState<TradesResponse>>) => {
    setPartialDataToObject(this.tradesResponse, state);
  };

  fetchPortfolio = async (isUpdating?: boolean) => {
    this.setPortfolio({ isLoading: false, isError: false });
    if (!isUpdating) this.setPortfolio({ data: null });

    const account = this.web3.account;
    if (!account) return;

    try {
      this.setPortfolio({ isLoading: true });

      const wallet = await getWallet(account);
      const { overview, positions } = await loadSupplementaryData(account);

      const data = normalizePortfolio(wallet, overview, positions);
      this.setPortfolio({ data, isLoading: false });
      if (this.tab !== 'activity') {
        this.setTrades({ data: { owner: account, trades: [] }, isError: false, isLoading: false });
      }
    } catch (err: any) {
      if (axios.isAxiosError(err) && err.response?.status === 404) {
        this.setPortfolio({ isLoading: false, data: null });
        this.setTrades({ data: { owner: account, trades: [] }, isLoading: false, isError: false });
        return;
      }

      console.error(err);
      this.setPortfolio({ isLoading: false, isError: true });
    }
  };

  fetchTrades = async () => {
    const account = this.web3.account;
    if (!account) return;

    try {
      this.setTrades({ isLoading: true, isError: false });
      const response = await getActivity(account, 1, EMPTY_TRADES.pagination.limit);
      this.setTrades({
        data: {
          owner: response.owner,
          pagination: response.pagination,
          trades: response.positions.map(normalizeTrade),
        },
        isLoading: false,
        isError: false,
      });
    } catch (err) {
      console.error('Failed to load activity:', err);
      this.setTrades({
        data: { owner: account, trades: [] },
        isLoading: false,
        isError: true,
      });
    }
  };

  fetchMoreTrades = async () => {
    const account = this.web3.account;
    const current = this.tradesResponse.data;
    if (!account || !current?.pagination || this.tradesResponse.isLoading) return;

    const { page, totalPages, limit } = current.pagination;
    if (page >= totalPages) return;

    try {
      this.setTrades({ isLoading: true, isError: false });
      const response = await getActivity(account, page + 1, limit);
      this.setTrades({
        data: {
          owner: response.owner,
          pagination: response.pagination,
          trades: [...current.trades, ...response.positions.map(normalizeTrade)],
        },
        isLoading: false,
        isError: false,
      });
    } catch (err) {
      console.error('Failed to load more activity:', err);
      this.setTrades({ isLoading: false, isError: true });
    }
  };

  updatePortfolioFromWallet = async (
    wallet: WalletResponse,
    strategyOverride?: CopyStrategyConfigDto
  ) => {
    const { overview, positions } = await loadSupplementaryData(wallet.owner);

    const nextWallet = strategyOverride
      ? { ...wallet, copyStrategyConfig: strategyOverride }
      : wallet;

    this.setPortfolio({
      data: normalizePortfolio(nextWallet, overview, positions),
      isLoading: false,
      isError: false,
    });
  };

  setTab = (tab: Tab) => {
    this.tab = tab;
  };
}
